import type { ReadonlyURLSearchParams } from 'next/navigation';
import { useSearchParams } from 'next/navigation';

import { useDiscount } from '@/queries/GetOneDiscount';
import type { SupportedRingGeneration } from '@/utils/discountUtils';
import { getSupportedDiscountGenerations } from '@/utils/discountUtils';
import stringifySearchParams from '@/utils/stringifySearchParams';

import { useCartContext } from '../contexts/CartProvider';

interface UseDiscountPageState {
  redirectUrl: string;
  isReady: boolean;
}

/** Whitelisted redirect URLs should be relative only */
const WHITELISTED_REDIRECT_URLS = ['/product/rings', '/store/rings'];

interface GetDiscountRedirectParams {
  supportedDiscountGenerations: Set<SupportedRingGeneration>;
  defaultPdpUrl: string | null | undefined;
  isReady: boolean;
  redirect: string;
  searchParams: ReadonlyURLSearchParams;
}

const formUrl = (
  redirect: string,
  searchParams: ReadonlyURLSearchParams,
): string => {
  const filteredSearchParams: Record<string, string> = Array.from(searchParams)
    .filter(([key]) => key !== 'redirectTo')
    .reduce(
      (acc, cur) => ({
        ...acc,
        [cur[0]]: cur[1],
      }),
      {},
    );
  const queryString = stringifySearchParams(filteredSearchParams);

  return queryString ? `${redirect}?${queryString}` : redirect;
};

//Helper function that takes the supported discount array and then finds out which rings are included in the discount
export const _ringGenCheck = ({
  supportedDiscountGenerations,
}: {
  supportedDiscountGenerations: Set<SupportedRingGeneration>;
}) => {
  const isGen3Path =
    supportedDiscountGenerations.size > 0 &&
    !supportedDiscountGenerations.has('gen4') &&
    !supportedDiscountGenerations.has('gen4-ceramic');

  const isGen4MetallicPath =
    supportedDiscountGenerations.size > 0 &&
    supportedDiscountGenerations.has('gen4');

  const isOR4CeramicPath =
    supportedDiscountGenerations.size > 0 &&
    supportedDiscountGenerations.has('gen4-ceramic');

  return { isGen3Path, isGen4MetallicPath, isOR4CeramicPath };
};

export const _getDiscountRedirect = ({
  supportedDiscountGenerations,
  defaultPdpUrl,
  isReady,
  redirect,
  searchParams,
}: GetDiscountRedirectParams): UseDiscountPageState => {
  /**
   * If we have single redirectTo value in query, and it is
   * a valid whitelist URL, use it as redirect URL
   *
   * e.g. discount/adiscountcode?redirectTo=/products/rings
   */
  if (
    redirect &&
    !Array.isArray(redirect) &&
    WHITELISTED_REDIRECT_URLS.includes(redirect)
  ) {
    return { redirectUrl: formUrl(redirect, searchParams), isReady };
  }

  // deprecated - exists on only 2 expired flo discounts
  // if the discount has a defaultPdpUrl, use it as redirect URL
  if (defaultPdpUrl) {
    return {
      redirectUrl: formUrl(`/product/rings/${defaultPdpUrl}`, searchParams),
      isReady,
    };
  }

  // if the discount has categories, check if not all-rings and gen4-rings
  const { isGen3Path, isGen4MetallicPath, isOR4CeramicPath } = _ringGenCheck({
    supportedDiscountGenerations,
  });

  const OR4_PATH = '/store/rings/oura-ring-4/silver';
  const OR4_CERAMIC_PATH = '/store/rings/oura-ring-4-ceramic/midnight';

  let redirectPath: string;
  if (isGen3Path) {
    redirectPath = '/product/rings/oura-gen3/heritage';
  } else if (isGen4MetallicPath) {
    redirectPath = OR4_PATH;
  } else if (isOR4CeramicPath) {
    redirectPath = OR4_CERAMIC_PATH;
  } else {
    redirectPath = OR4_PATH;
  }

  return {
    redirectUrl: formUrl(redirectPath, searchParams),
    isReady,
  };
};

const useDiscountPage = (
  handle: string | null | undefined,
): UseDiscountPageState => {
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirectTo') ?? '';
  const { urlPromotionSuccess } = useCartContext();
  const { data, isPending, isSuccess } = useDiscount(
    urlPromotionSuccess && handle ? handle : '',
  );

  const supportedDiscountGenerations = getSupportedDiscountGenerations(data);
  const defaultPdpUrl = data?.content?.discount?.defaultPdpUrl;
  const isReady = !isPending && isSuccess;

  return _getDiscountRedirect({
    supportedDiscountGenerations,
    defaultPdpUrl,
    isReady,
    redirect,
    searchParams,
  });
};

export default useDiscountPage;
